import { FC, FormEvent, useCallback, useEffect, useState } from "react";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";

import { DOBSelect, GradYears, Height } from "../Dashboard/Athletes/Options";
import FormDropdown from "../Dashboard/Dropdown";
import SchoolSelect from "../Dashboard/Athletes/SchoolSelect";
import { User } from "../../models/User";
import { authClasses } from "../../pages/Auth/authClasses";
import { db } from "../../firebase";

type AthleteRecord = {
	id: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	school?: string | null;
	gradYear?: string;
	season?: string;
	dob?: Date | { toDate: () => Date };
	feet?: string;
	inches?: string;
	shirtSize?: string;
	shortSize?: string;
};

type AthleteDraft = {
	firstName: string;
	lastName: string;
	email: string;
	school: string | null;
	gradYear: string;
	season: string;
	month: string;
	day: string;
	year: string;
	feet: string;
	inches: string;
	shirtSize: string;
	shortSize: string;
};

type AthleteProfileEditorProps = {
	user: User;
};

const monthNames = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

const getAthleteDOB = (value: AthleteRecord["dob"]) => {
	if (!value) {
		return null;
	}
	if (value instanceof Date) {
		return value;
	}
	if (typeof value.toDate === "function") {
		return value.toDate();
	}
	return null;
};

const createDraft = (athlete: AthleteRecord): AthleteDraft => {
	const dob = getAthleteDOB(athlete.dob);

	return {
		firstName: athlete.firstName ?? "",
		lastName: athlete.lastName ?? "",
		email: athlete.email ?? "",
		school: athlete.school ?? null,
		gradYear: athlete.gradYear ?? "HS Grad Year",
		season: athlete.season ?? "Season",
		month: dob ? monthNames[dob.getMonth()] : "Month",
		day: dob ? String(dob.getDate()) : "Day",
		year: dob ? String(dob.getFullYear()) : "Year",
		feet: athlete.feet ?? "Height (ft)",
		inches: athlete.inches ?? "Height (in)",
		shirtSize: athlete.shirtSize ?? "Shirt Size",
		shortSize: athlete.shortSize ?? "Shorts Size",
	};
};

const buildDOB = (draft: AthleteDraft) => {
	const monthIndex = monthNames.indexOf(draft.month);
	const parsedDay = Number(draft.day);
	const parsedYear = Number(draft.year);

	if (
		monthIndex < 0 ||
		!Number.isInteger(parsedDay) ||
		!Number.isInteger(parsedYear)
	) {
		return null;
	}

	const dob = new Date(parsedYear, monthIndex, parsedDay);
	if (
		dob.getFullYear() !== parsedYear ||
		dob.getMonth() !== monthIndex ||
		dob.getDate() !== parsedDay
	) {
		return null;
	}

	return dob;
};

const isDraftInvalid = (draft: AthleteDraft) =>
	draft.firstName.trim() === "" ||
	draft.lastName.trim() === "" ||
	draft.school === null ||
	draft.season === "Season" ||
	draft.month === "Month" ||
	draft.day === "Day" ||
	draft.year === "Year" ||
	draft.gradYear === "HS Grad Year" ||
	draft.feet === "Height (ft)" ||
	draft.inches === "Height (in)" ||
	draft.shirtSize === "Shirt Size" ||
	draft.shortSize === "Shorts Size";

const AthleteProfileEditor: FC<AthleteProfileEditorProps> = ({ user }) => {
	const { buttonSmall, disabledButton, input } = authClasses;
	const [athletes, setAthletes] = useState<AthleteRecord[]>([]);
	const [loading, setLoading] = useState(true);
	const [savingID, setSavingID] = useState("");
	const [editingID, setEditingID] = useState("");
	const [draft, setDraft] = useState<AthleteDraft | null>(null);
	const [message, setMessage] = useState("");

	const fetchAthletes = useCallback(async () => {
		setLoading(true);
		const snapshot = await getDocs(collection(db, "users", user.id, "athletes"));
		const nextAthletes: AthleteRecord[] = snapshot.docs
			.map(
				(athleteDoc) =>
					({
						id: athleteDoc.id,
						...athleteDoc.data(),
					}) as AthleteRecord,
			)
			.sort((a, b) =>
				`${a.firstName ?? ""} ${a.lastName ?? ""}`.localeCompare(
					`${b.firstName ?? ""} ${b.lastName ?? ""}`,
				),
			);
		setAthletes(nextAthletes);
		setLoading(false);
	}, [user.id]);

	useEffect(() => {
		fetchAthletes();
	}, [fetchAthletes]);

	const updateDraft = (values: Partial<AthleteDraft>) => {
		setDraft((current) => (current ? { ...current, ...values } : current));
	};

	const startEditing = (athlete: AthleteRecord) => {
		setMessage("");
		setEditingID(athlete.id);
		setDraft(createDraft(athlete));
	};

	const cancelEditing = () => {
		setMessage("");
		setEditingID("");
		setDraft(null);
	};

	const saveAthlete = async (event: FormEvent) => {
		event.preventDefault();
		if (!draft || !editingID) {
			return;
		}

		const dob = buildDOB(draft);
		if (isDraftInvalid(draft) || dob === null) {
			setMessage("Complete all required athlete fields before saving.");
			return;
		}

		setSavingID(editingID);
		setMessage("");
		try {
			await updateDoc(doc(db, "users", user.id, "athletes", editingID), {
				firstName: draft.firstName.trim(),
				lastName: draft.lastName.trim(),
				email: draft.email.trim(),
				school: draft.school,
				gradYear: draft.gradYear,
				season: draft.season,
				dob,
				feet: draft.feet,
				inches: draft.inches,
				shirtSize: draft.shirtSize,
				shortSize: draft.shortSize,
			});
			await fetchAthletes();
			setEditingID("");
			setDraft(null);
			setMessage("Athlete saved.");
		} catch {
			setMessage("Could not save athlete. Try again or contact Sidewinder.");
		} finally {
			setSavingID("");
		}
	};

	return (
		<section className="grid gap-4 border-t border-white/10 pt-5">
			<div>
				<h2 className="m-0 text-xl text-white">Child Athletes</h2>
				<p className="m-0 text-sm text-slate-400">
					Update athlete details used for club tryouts, clinics, and Hatchlings.
				</p>
			</div>

			{message ? <small className="text-slate-300">{message}</small> : null}

			{loading ? (
				<p className="m-0 text-sm text-slate-400">Loading athletes...</p>
			) : athletes.length === 0 ? (
				<p className="m-0 text-sm text-slate-400">
					No child athletes have been added yet.
				</p>
			) : (
				<div className="grid gap-3">
					{athletes.map((athlete) => {
						const isEditing = editingID === athlete.id && draft;
						const isSaving = savingID === athlete.id;

						return (
							<div
								className="rounded-md border border-white/10 bg-black/20 p-4"
								key={athlete.id}
							>
								{isEditing ? (
									<form className="grid gap-4" onSubmit={saveAthlete}>
										<div className="flex flex-wrap items-center justify-between gap-3">
											<h3 className="m-0 text-lg text-white">
												Edit {athlete.firstName} {athlete.lastName}
											</h3>
											<button
												className="min-h-[40px] rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm font-bold text-slate-200 transition hover:border-accent-color-2 hover:text-accent-color-2"
												type="button"
												onClick={cancelEditing}
											>
												Cancel
											</button>
										</div>

										<div className="grid gap-3 md:grid-cols-2">
											<input
												className={input}
												placeholder="First name"
												value={draft.firstName}
												onChange={(event) =>
													updateDraft({ firstName: event.target.value })
												}
											/>
											<input
												className={input}
												placeholder="Last name"
												value={draft.lastName}
												onChange={(event) =>
													updateDraft({ lastName: event.target.value })
												}
											/>
										</div>

										<input
											className={input}
											placeholder="Child Athlete Email (optional)"
											type="email"
											value={draft.email}
											onChange={(event) =>
												updateDraft({ email: event.target.value })
											}
										/>

										<div className="grid gap-1.5">
											<small className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
												Date of Birth
											</small>
											<DOBSelect
												month={draft.month}
												setMonth={(month: string) => updateDraft({ month })}
												day={draft.day}
												setDay={(day: string) => updateDraft({ day })}
												year={draft.year}
												setYear={(year: string) => updateDraft({ year })}
											/>
										</div>

										<div className="grid gap-3 md:grid-cols-2">
											<SchoolSelect
												defaultValue={draft.school}
												setSchool={(school: string | null) =>
													updateDraft({ school })
												}
											/>
											<FormDropdown
												options={GradYears}
												value={draft.gradYear}
												setVal={(gradYear) => updateDraft({ gradYear })}
											/>
										</div>

										<div className="grid gap-3 md:grid-cols-2">
											<div className="grid gap-2">
												<span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
													Height
												</span>
												<Height
													feet={draft.feet}
													setFeet={(feet: string) => updateDraft({ feet })}
													inches={draft.inches}
													setInches={(inches: string) =>
														updateDraft({ inches })
													}
												/>
											</div>
											<div className="grid gap-2">
												<span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
													Season
												</span>
												<FormDropdown
													options={["Season", "Boys", "Girls"]}
													value={draft.season}
													setVal={(season) => updateDraft({ season })}
												/>
											</div>
											<div className="grid gap-2">
												<span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
													Shirt Size
												</span>
												<FormDropdown
													options={["Shirt Size", "S", "M", "L", "XL", "2XL"]}
													value={draft.shirtSize}
													setVal={(shirtSize) => updateDraft({ shirtSize })}
												/>
											</div>
											<div className="grid gap-2">
												<span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
													{draft.season === "Girls"
														? "Spandex/Legging Size"
														: "Shorts Size"}
												</span>
												<FormDropdown
													options={["Shorts Size", "S", "M", "L", "XL", "2XL"]}
													value={draft.shortSize}
													setVal={(shortSize) => updateDraft({ shortSize })}
												/>
											</div>
										</div>

										<div className="flex flex-wrap justify-end gap-3">
											<button
												className={isDraftInvalid(draft) ? disabledButton : buttonSmall}
												disabled={isSaving || isDraftInvalid(draft)}
											>
												{isSaving ? "Saving..." : "Save Athlete"}
											</button>
										</div>
									</form>
								) : (
									<div className="flex flex-wrap items-center justify-between gap-3">
										<div>
											<strong className="block text-white">
												{athlete.firstName} {athlete.lastName}
											</strong>
											<span className="text-sm text-slate-400">
												{athlete.season ?? "Season not set"}
												{athlete.school ? ` · ${athlete.school}` : ""}
												{athlete.gradYear ? ` · ${athlete.gradYear}` : ""}
											</span>
										</div>
										<button
											className={buttonSmall}
											type="button"
											onClick={() => startEditing(athlete)}
										>
											Edit Athlete
										</button>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</section>
	);
};

export default AthleteProfileEditor;
